(() => {
  // Delegated handlers also work after Shopify's theme editor or Section Rendering replaces markup.
  if (window.DailyXTheme) return;
  const requests = new WeakMap();
  const recommendationRequests = new WeakMap();
  const modelViewers = new WeakMap();
  const findAll = (scope, selector) => [
    ...(scope instanceof Element && scope.matches(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector),
  ];

  function normalizeQuantity(input, value) {
    const min = Number(input.min) || 1;
    const step = Number(input.step) || 1;
    const max = Number(input.max) || Infinity;
    const bounded = Math.max(min, Math.min(max, Number(value) || min));
    return String(min + Math.floor((bounded - min) / step) * step);
  }

  function setMenu(open) {
    const panel = document.querySelector('[data-dx-mobile-panel]');
    if (!panel) return;
    panel.classList.toggle('is-open', open);
    panel.setAttribute('aria-hidden', String(!open));
    document.querySelectorAll('[data-dx-menu-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', String(open));
    });
    document.body.style.overflow = open ? 'hidden' : '';
  }

  function pauseMedia(scope) {
    scope.querySelectorAll('video').forEach((video) => video.pause());
    scope.querySelectorAll('model-viewer').forEach((model) => modelViewers.get(model)?.pause());
    scope.querySelectorAll('iframe').forEach((frame) => {
      // Reset only the outgoing player; it must not keep playing inside a hidden slide.
      if (frame.src) frame.src = frame.src;
    });
  }

  function initModels(scope) {
    const models = [...scope.querySelectorAll('model-viewer')];
    if (!models.length || !window.Shopify?.loadFeatures) return;
    window.Shopify.loadFeatures([{
      name: 'model-viewer-ui',
      version: '1.0',
      onLoad: (error) => {
        if (error || !window.Shopify.ModelViewerUI) return;
        models.forEach((model) => {
          if (model.isConnected && !modelViewers.has(model)) {
            modelViewers.set(model, new window.Shopify.ModelViewerUI(model));
          }
        });
      },
    }]);
  }

  async function loadRecommendations(element) {
    if (!element.dataset.productId || !element.dataset.url) return;
    recommendationRequests.get(element)?.abort();
    const controller = new AbortController();
    recommendationRequests.set(element, controller);
    const url = new URL(element.dataset.url, window.location.origin);
    url.searchParams.set('section_id', element.dataset.sectionId);
    url.searchParams.set('product_id', element.dataset.productId);
    url.searchParams.set('limit', element.dataset.limit);
    url.searchParams.set('intent', element.dataset.intent);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = html.querySelector('[data-dx-recommendations]');
      if (!element.isConnected || recommendationRequests.get(element) !== controller) return;
      element.innerHTML = incoming?.innerHTML || '';
      element.hidden = !element.querySelector('.dx-card');
    } catch (error) {
      if (error.name !== 'AbortError' && recommendationRequests.get(element) === controller) element.hidden = true;
    }
  }

  async function updateVariant(input) {
    const section = input.closest('[data-dx-product]');
    if (!section) return;
    requests.get(section)?.abort();
    const controller = new AbortController();
    requests.set(section, controller);
    const destination = new URL(input.dataset.productUrl || section.dataset.productUrl, window.location.origin);
    if (destination.origin !== window.location.origin) return;
    // Retain attribution and preview parameters while changing only the selected product/options.
    new URL(window.location.href).searchParams.forEach((value, key) => {
      if (!['variant', 'option_values', 'section_id', 'sections'].includes(key)) destination.searchParams.set(key, value);
    });
    destination.searchParams.delete('variant');
    const values = [...section.querySelectorAll('[data-dx-option-value-id]:checked')].map((option) => option.dataset.dxOptionValueId);
    destination.searchParams.set('option_values', values.join(','));
    if (destination.pathname !== new URL(section.dataset.productUrl, window.location.origin).pathname) {
      // A combined-listing sibling needs its own metadata, app context and sections, not a partial page swap.
      window.location.assign(destination.href);
      return;
    }
    const requestUrl = new URL(destination);
    requestUrl.searchParams.set('section_id', section.dataset.sectionId);
    const oldQuantity = section.querySelector('input[name="quantity"]')?.value;
    const status = section.querySelector('[data-dx-product-status]');
    section.setAttribute('aria-busy', 'true');
    section.dataset.variantPending = 'true';
    section.querySelectorAll('[data-dx-add]').forEach((button) => { button.disabled = true; });
    const dynamicCheckout = section.querySelector('[data-dx-dynamic-checkout]');
    if (dynamicCheckout) dynamicCheckout.hidden = true;
    const retry = section.querySelector('[data-dx-product-retry]');
    if (retry) retry.hidden = true;
    if (status) status.textContent = status.dataset.loading;
    try {
      const response = await fetch(requestUrl, { signal: controller.signal });
      if (!response.ok) throw new Error('Product option unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = html.querySelector('[data-dx-product]');
      if (!next || next.dataset.sectionId !== section.dataset.sectionId) throw new Error('Invalid product section');
      if (!section.isConnected || requests.get(section) !== controller) return;
      const quantity = next.querySelector('input[name="quantity"]');
      if (quantity && oldQuantity) {
        quantity.value = normalizeQuantity(quantity, oldQuantity);
      }
      if (next.dataset.productId === section.dataset.productId) {
        // Keep mounted review/subscription widgets and their state when only the variant changes.
        const incomingApps = [...next.querySelectorAll('[data-dx-product-app]')];
        section.querySelectorAll('[data-dx-product-app]').forEach((app) => {
          incomingApps.find((incoming) => incoming.dataset.dxProductApp === app.dataset.dxProductApp)?.replaceWith(app);
        });
      }
      pauseMedia(section);
      section.replaceWith(next);
      if (next.dataset.variantId) {
        destination.searchParams.delete('option_values');
        destination.searchParams.set('variant', next.dataset.variantId);
      }
      window.history.replaceState({}, '', destination);
      const focusTarget = document.getElementById(input.id) || next.querySelector('[data-dx-option-value-id]:checked');
      focusTarget?.focus({ preventScroll: true });
      initModels(next);
      window.Shopify?.PaymentButton?.init?.();
      if (next.dataset.productId !== section.dataset.productId) {
        document.querySelectorAll('[data-dx-recommendations]').forEach((element) => {
          element.dataset.productId = next.dataset.productId;
          loadRecommendations(element);
        });
      }
      next.dispatchEvent(new CustomEvent('dx:variant:change', { bubbles: true, detail: { productId: next.dataset.productId, variantId: next.dataset.variantId } }));
    } catch (error) {
      if (error.name === 'AbortError' || requests.get(section) !== controller || !section.isConnected) return;
      // Fail closed: the previously selected SKU must never be added under a new option label.
      const variantInput = section.querySelector('input[name="id"]');
      if (variantInput) variantInput.disabled = true;
      if (status) status.textContent = status.dataset.error;
      if (retry) { retry.href = destination.href; retry.hidden = false; }
    } finally {
      if (section.isConnected && requests.get(section) === controller) section.removeAttribute('aria-busy');
    }
  }

  function init(scope = document) {
    findAll(scope, '[data-dx-product]').forEach(initModels);
    findAll(scope, '[data-dx-recommendations]').forEach(loadRecommendations);
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const menuButton = event.target.closest('[data-dx-menu-toggle]');
    if (menuButton) {
      const panel = document.querySelector('[data-dx-mobile-panel]');
      setMenu(!panel?.classList.contains('is-open'));
    }
    const quantityButton = event.target.closest('[data-dx-qty] button[data-delta]');
    if (quantityButton) {
      const input = quantityButton.closest('[data-dx-qty]').querySelector('input');
      if (!input) return;
      const step = Number(input.step) || 1;
      const min = Number(input.min) || 1;
      const delta = quantityButton.dataset.delta === 'plus' ? step : -step;
      input.value = normalizeQuantity(input, (Number(input.value) || min) + delta);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const thumbnail = event.target.closest('[data-dx-media-target]');
    if (thumbnail) {
      const section = thumbnail.closest('[data-dx-product]');
      const target = document.getElementById(thumbnail.dataset.dxMediaTarget);
      if (!section || !target || !section.contains(target)) return;
      section.querySelectorAll('[data-dx-media]').forEach((media) => {
        if (!media.hidden && media !== target) pauseMedia(media);
        media.hidden = media !== target;
      });
      section.querySelectorAll('[data-dx-media-target]').forEach((button) => button.setAttribute('aria-current', String(button === thumbnail)));
    }
    const legacySticky = event.target.closest('[data-dx-sticky-submit]');
    if (legacySticky) document.querySelector('[data-dx-product-form], #DxProductForm')?.requestSubmit();
  });

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.matches('[data-dx-option-value-id]')) updateVariant(event.target);
    if (event.target.matches('[data-dx-sort]')) event.target.form?.requestSubmit();
  });

  document.addEventListener('submit', (event) => {
    if (event.target.matches('[data-dx-product-form]') && event.target.closest('[data-dx-product]')?.dataset.variantPending === 'true') event.preventDefault();
  });

  document.addEventListener('formdata', (event) => {
    if (!event.target.matches('[data-dx-filter-form]')) return;
    for (const [key, value] of [...event.formData.entries()]) {
      if (key.startsWith('filter.') && value === '') event.formData.delete(key);
    }
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    findAll(event.target, '[data-dx-product]').forEach((section) => { requests.get(section)?.abort(); pauseMedia(section); });
    findAll(event.target, '[data-dx-recommendations]').forEach((section) => recommendationRequests.get(section)?.abort());
  });
  window.DailyXTheme = { init, setMenu };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
